// Make sure to install react-quill: npm install react-quill
// Removed react-quill for plain text content
import { functions, httpsCallable, storage } from '../../config/firebase-config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PostBlogPage.css';
import '../blog/BlogPage.css'; // Import blog styles for exact preview matching

// Helper to process [text](url) links in preview
function processLinks(text: string): React.ReactNode {
  // Image markdown: ![alt|width=400|height=200](url) or ![](url)
  // Clickable image: [![](url)](link)
  const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  // Clickable image: [![](url)](link)
  const clickableImageRegex = /\[!\[([^\]]*)\]\(([^)]+)\)\]\(([^)]+)\)/g;
  let clickableMatch;
  while ((clickableMatch = clickableImageRegex.exec(text)) !== null) {
    if (clickableMatch.index > lastIndex) {
      parts.push(text.slice(lastIndex, clickableMatch.index));
    }
    // Parse alt and size from clickableMatch[1]
    let alt = clickableMatch[1] || '';
    let imgUrl = clickableMatch[2];
    let linkUrl = clickableMatch[3];
    let width, height;
    const sizeMatch = alt.match(/\|\s*width=(\d+)/);
    if (sizeMatch) width = sizeMatch[1];
    const heightMatch = alt.match(/\|\s*height=(\d+)/);
    if (heightMatch) height = heightMatch[1];
    alt = alt.split('|')[0].trim();
    parts.push(
      <a href={linkUrl} target="_blank" rel="noopener noreferrer">
        <img src={imgUrl} alt={alt} style={{ maxWidth: width ? `${width}px` : '400px', height: height ? `${height}px` : 'auto', borderRadius: '8px', margin: '12px 0', display: 'block' }} />
      </a>
    );
    lastIndex = clickableImageRegex.lastIndex;
  }

  // Process remaining text for images
  let remaining = text.slice(lastIndex);
  lastIndex = 0;
  while ((match = imageRegex.exec(remaining)) !== null) {
    if (match.index > lastIndex) {
      parts.push(remaining.slice(lastIndex, match.index));
    }
    // Parse alt and size from match[1]
    let alt = match[1] || '';
    let imgUrl = match[2];
    let width, height;
    const sizeMatch = alt.match(/\|\s*width=(\d+)/);
    if (sizeMatch) width = sizeMatch[1];
    const heightMatch = alt.match(/\|\s*height=(\d+)/);
    if (heightMatch) height = heightMatch[1];
    alt = alt.split('|')[0].trim();
    parts.push(
      <img src={imgUrl} alt={alt} style={{ maxWidth: width ? `${width}px` : '400px', height: height ? `${height}px` : 'auto', borderRadius: '8px', margin: '12px 0', display: 'block' }} />
    );
    lastIndex = imageRegex.lastIndex;
  }

  // Process remaining text for links
  remaining = remaining.slice(lastIndex);
  lastIndex = 0;
  while ((match = linkRegex.exec(remaining)) !== null) {
    if (match.index > lastIndex) {
      parts.push(remaining.slice(lastIndex, match.index));
    }
    parts.push(
      <a href={match[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#06c', textDecoration: 'none' }}>
        {match[1]}
      </a>
    );
    lastIndex = linkRegex.lastIndex;
  }
  if (lastIndex < remaining.length) {
    parts.push(remaining.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}



const PostBlogPage: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    author: '',
    category: '',
    date: new Date().toISOString().slice(0, 10),
    readTime: '',
    summary: '',
    content: '',
    image: null as File | null,
    imageUrl: '',
  });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [message, setMessage] = useState('');
  const [showFormatting, setShowFormatting] = useState(false);

  // Create memoized image URL for preview
  const previewImageUrl = useMemo(() => {
    if (form.image) {
      try {
        console.log('Creating object URL for image:', form.image.name, form.image.size);
        const url = URL.createObjectURL(form.image);
        console.log('Created object URL:', url);
        return url;
      } catch (error) {
        console.error('Error creating object URL:', error);
        return null;
      }
    }
    console.log('No image to create URL for');
    return null;
  }, [form.image]);

  // Cleanup object URL when component unmounts or image changes
  useEffect(() => {
    return () => {
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
    };
  }, [previewImageUrl]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };


  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setForm({ ...form, content: e.target.value });
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setForm({ ...form, image: file });
    }
  };

  const uploadImage = async () => {
    if (!form.image) return '';
    const imageRef = ref(storage, `blog-thumbnails/${Date.now()}_${form.image.name}`);
    await uploadBytes(imageRef, form.image);
    return await getDownloadURL(imageRef);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      let imageUrl = form.imageUrl;
      if (form.image && !form.imageUrl) {
        imageUrl = await uploadImage();
      }
      const saveBlog = httpsCallable(functions, 'saveBlog');
      const result = await saveBlog({
        title: form.title,
        author: form.author,
        category: form.category,
        date: form.date,
        readTime: form.readTime,
        summary: form.summary,
        content: form.content,
        image: imageUrl,
      });
      // Expect backend to return the new document ID
      // Safely extract id/docId from result.data (which may be unknown type)
      const data = result.data as { id?: string; docId?: string } | undefined;
      const docId = data?.id || data?.docId;
      if (docId) {
        // Navigate directly to the new blog details page for best UX
        navigate(`/blogs/${docId}`);
        // Optionally, you can show a toast or message before navigating if needed
      } else {
        setMessage('Blog posted successfully!');
        setForm({ ...form, image: null, imageUrl: '', content: '' });
      }
    } catch (err) {
      setMessage('Error posting blog.');
    }
    setLoading(false);
  };

  return (
    <div className="post-blog-form-container">
      <h2 className="post-blog-form-title">Post a New Blog</h2>
      
      {/* Toggle Controls */}
      <div className="view-toggle-controls">
        <button 
          type="button" 
          className={`toggle-btn ${showForm ? 'active' : ''}`}
          onClick={() => {
            setShowForm(true);
            setShowPreview(false);
          }}
        >
          Edit Form
        </button>
        <button 
          type="button" 
          className={`toggle-btn ${showPreview ? 'active' : ''}`}
          onClick={() => {
            setShowForm(false);
            setShowPreview(true);
          }}
        >
          Live Preview
        </button>

      </div>

      {/* Content Wrapper */}
      <div className="single-view">
        {/* Form Section */}
        {showForm && (
          <form className="post-blog-form" onSubmit={handleSubmit} autoComplete="off">
        <input className="post-blog-input" name="title" placeholder="Title" title="Blog Title" value={form.title} onChange={handleChange} required />
        <input className="post-blog-input" name="author" placeholder="Author" title="Author Name" value={form.author} onChange={handleChange} required />
        <input className="post-blog-input" name="category" placeholder="Category" title="Category" value={form.category} onChange={handleChange} required />
        <input className="post-blog-input" name="date" type="date" title="Blog Date" value={form.date} onChange={handleChange} required />
        <input className="post-blog-input" name="readTime" placeholder="Read Time (e.g. 5 min)" value={form.readTime} onChange={handleChange} />
        <textarea className="post-blog-textarea" name="summary" placeholder="Summary" value={form.summary} onChange={handleChange} required />
        <div className="content-input-section">
          <div className="content-header">
            <label>Blog Content</label>
            <button 
              type="button" 
              className="formatting-guide-btn"
              onClick={() => setShowFormatting(!showFormatting)}
            >
              {showFormatting ? 'Hide' : 'Show'} Formatting Guide
            </button>
          </div>
          {showFormatting && (
            <div className="formatting-guide">
              <h4>Formatting Reference:</h4>
              <div className="formatting-examples">
                <div className="format-item">
                  <strong>Headings:</strong>
                  <code>UPPERCASE TEXT (6+ chars)</code>
                  <span>→ Large heading</span>
                </div>
                <div className="format-item">
                  <strong>Subheadings:</strong>
                  <code>Title Case Text</code>
                  <span>→ Section heading</span>
                </div>
                <div className="format-item">
                  <strong>Numbered Sections:</strong>
                  <code>1. Section Name</code>
                  <span>→ Numbered heading</span>
                </div>
                <div className="format-item">
                  <strong>Bold Labels:</strong>
                  <code>Label: content here</code>
                  <span>→ <strong>Label:</strong> content here</span>
                </div>
                <div className="format-item">
                  <strong>Lists:</strong>
                  <code>- List item<br/>- Another item</code>
                  <span>→ Bullet points</span>
                </div>
                <div className="format-item">
                  <strong>Quotes:</strong>
                  <code>"Quote text"<br/>— Author Name</code>
                  <span>→ Styled blockquote</span>
                </div>
                <div className="format-item">
                  <strong>Links:</strong>
                  <code>[link text](url)</code>
                  <span>→ Clickable link</span>
                </div>
                <div className="format-item">
                  <strong>Images:</strong>
                  <code>![alt text](image-url)</code>
                  <span>→ Embedded image</span>
                </div>
                <div className="format-item">
                  <strong>Image Sizes:</strong>
                  <code>![alt|width=400|height=300](url)</code>
                  <span>→ Sized image</span>
                </div>
                <div className="format-item">
                  <strong>Clickable Images:</strong>
                  <code>[![alt](image-url)](link-url)</code>
                  <span>→ Image with link</span>
                </div>
              </div>
            </div>
          )}
          <textarea
            className="post-blog-textarea"
            name="content"
            placeholder={`Blog Content - Use the formatting guide above for proper styling

Example:
INTRODUCTION

Welcome to our blog! This is how you write content.

Key Benefits:
- Point one here
- Point two here
- Point three here

"This is a quote from someone important"
— Author Name

For more information, visit [our website](https://example.com).`}
            value={form.content}
            onChange={handleContentChange}
            required
            rows={12}
          />
        </div>
        <input className="post-blog-input" type="file" accept="image/*" title="Blog Featured Image" onChange={handleImageChange} />
        {form.image && <div className="post-blog-image-selected">Selected image: {form.image.name}</div>}
            <div className="post-blog-btn-row">
              <button type="submit" className="post-blog-btn primary" disabled={loading}>
                {loading ? 'Posting...' : 'Publish Blog'}
              </button>
            </div>
          </form>
        )}
      
      {message && <div className="post-blog-message">{message}</div>}
      
      {/* Preview Section */}
      {showPreview && (
        <div className="post-blog-preview">
          {/* Blog Summary Tile Preview */}
          <div className="preview-section">
            <h4 className="preview-section-title">How it appears on Blog Page</h4>
            <div className="blog-summary-tile-preview">
              <div className="blog-summary-tile">
                <div className="blog-summary-image-wrapper">
                  {previewImageUrl ? (
                    <img 
                      className="blog-summary-image" 
                      src={previewImageUrl} 
                      alt={form.category} 
                    />
                  ) : (
                    <div className="placeholder-image">
                      <span>Upload Image</span>
                    </div>
                  )}
                  <span className="blog-summary-category">{form.category || 'Category'}</span>
                </div>
                <div className="blog-summary-content">
                  <div className="blog-summary-title">{form.title || 'Blog Title'}</div>
                  <div className="blog-summary-author">By {form.author || 'Author'}</div>
                  <svg className="blog-summary-divider" width="333" height="2" viewBox="0 0 333 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 1H333" stroke="#E5E7EB"/>
                  </svg>
                  <div className="blog-summary-meta">
                    <span>{form.date || 'Date'}</span>
                    <span className="blog-summary-dot">•</span>
                    <span>{form.readTime || 'Read Time'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Blog Details Preview - Full Width Layout */}
          <div className="preview-section full-width-preview">
            <h4 className="preview-section-title">Full Blog Content</h4>
            <div className="full-blog-preview-container">
            <div className="full-blog-preview">
              {/* Left side - Blog Summary Tile */}
              <div className="preview-left">
                <div className="blog-summary-tile">
                  <div className="blog-summary-image-wrapper">
                    {form.image ? (
                      <img 
                        className="blog-summary-image" 
                        src={URL.createObjectURL(form.image)} 
                        alt={form.category} 
                      />
                    ) : (
                      <div className="placeholder-image">
                        <span>Upload Image</span>
                      </div>
                    )}
                    <span className="blog-summary-category">{form.category || 'Category'}</span>
                  </div>
                  <div className="blog-summary-content">
                    <div className="blog-summary-title">{form.title || 'Blog Title'}</div>
                    <div className="blog-summary-author">By {form.author || 'Author'}</div>
                    <svg className="blog-summary-divider" width="333" height="2" viewBox="0 0 333 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 1H333" stroke="#E5E7EB"/>
                    </svg>
                    <div className="blog-summary-meta">
                      <span>{form.date || 'Date'}</span>
                      <span className="blog-summary-dot">•</span>
                      <span>{form.readTime || 'Read Time'}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right side - Blog Content */}
              <div className="preview-right">
                <h1 className="preview-title">{form.title || 'Blog Title'}</h1>
                <div className="preview-content blog-details-content">
            
            <div className="blog-content-body">
            {(() => {
              const lines = form.content?.split('\n') || [];
              const elements = [];
              let inList = false;
              let listItems: JSX.Element[] = [];
              for (let idx = 0; idx < lines.length; idx++) {
                let trimmed = lines[idx].trim();
                // Bold before colon
                const colonRegex = /^(.+?)\s*:\s*(.+)$/;
                const colonMatch = trimmed.match(colonRegex);
                if (colonMatch) {
                  elements.push(
                    <p key={idx} className="blog-paragraph">
                      <span style={{ fontWeight: 600, color: '#1F2937' }}>{colonMatch[1]}:</span> {processLinks(colonMatch[2])}
                    </p>
                  );
                  continue;
                }
                if (!trimmed) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`} className="blog-list" style={{ marginBottom: '16px' }}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  continue;
                }
                // Headings
                if (idx === 0 && elements.length === 0 && trimmed.length > 10) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`} className="blog-list">{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  elements.push(<h2 className="blog-main-title" key={idx}>{processLinks(trimmed)}</h2>);
                  continue;
                }
                // Quotes
                if (/^['"“”]/.test(trimmed)) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`} className="blog-list" style={{ marginBottom: '16px' }}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  const nextLine = lines[idx + 1]?.trim();
                  if (nextLine && /^\s*(—|–|--|-|—)\s*[A-Z]/.test(nextLine)) {
                    elements.push(
                      <blockquote key={idx} className="blog-quote" style={{ marginBottom: '18px', borderLeft: '4px solid #1F2937', paddingLeft: '12px', color: '#444' }}>
                        {processLinks(trimmed)}
                        <div className="blog-quote-author" style={{ fontStyle: 'italic', marginTop: '6px', color: '#888' }}>{processLinks(nextLine)}</div>
                      </blockquote>
                    );
                    idx++;
                    continue;
                  } else {
                    elements.push(<blockquote key={idx} className="blog-quote">{processLinks(trimmed)}</blockquote>);
                    continue;
                  }
                }
                // All uppercase lines as headings
                if (/^[A-Z][A-Z\s—-]+$/.test(trimmed) && trimmed.length > 6) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`} className="blog-list" style={{ marginBottom: '16px' }}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  elements.push(<h3 className="blog-heading" key={idx} style={{ marginBottom: '16px', color: '#1F2937' }}>{processLinks(trimmed)}</h3>);
                  continue;
                }
                // Numbered headings
                if (/^\d+\.\s*[A-Z][a-zA-Z\s-]+$/.test(trimmed)) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`} className="blog-list" style={{ marginBottom: '16px' }}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  elements.push(<h3 className="blog-heading" key={idx} style={{ marginBottom: '16px', color: '#1F2937' }}>{processLinks(trimmed)}</h3>);
                  continue;
                }
                // Title Case detection
                const words = trimmed.split(' ');
                const titleCaseCount = words.filter((w: string) => w.length > 2 && /^[A-Z][a-z]/.test(w)).length;
                if (words.length >= 2 && words.length <= 8 && titleCaseCount / words.length > 0.5 && !trimmed.includes('.') && trimmed.length < 60) {
                  if (inList) {
                    elements.push(<ul key={`list-${idx}`} className="blog-list" style={{ marginBottom: '16px' }}>{listItems}</ul>);
                    listItems = [];
                    inList = false;
                  }
                  elements.push(<h3 className="blog-heading" key={idx} style={{ marginBottom: '16px', color: '#1F2937' }}>{processLinks(trimmed)}</h3>);
                  continue;
                }
                // List items
                if (/^\s*[-*•]\s+\w/.test(trimmed) && !/^\s*(—|–|--)/.test(trimmed)) {
                  inList = true;
                  const cleanedText = trimmed.replace(/^\s*[-*•]\s+/, '');
                  listItems.push(
                    <li key={`item-${idx}`} className="blog-list-item">
                      {processLinks(cleanedText)}
                    </li>
                  );
                  continue;
                } else if (inList) {
                  elements.push(<ul key={`list-${idx}`} className="blog-list" style={{ marginBottom: '16px' }}>{listItems}</ul>);
                  listItems = [];
                  inList = false;
                }
                // Paragraph
                elements.push(<p className="blog-paragraph" key={idx}>{processLinks(trimmed)}</p>);
              }
              if (inList && listItems.length > 0) {
                elements.push(<ul key="final-list" className="blog-list">{listItems}</ul>);
              }
              return elements;
            })()}
            </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}
      </div>
      
    </div>
  );
};

export default PostBlogPage;
